import {
  booleanAttribute,
  computed,
  Component,
  ElementRef,
  DoCheck,
  input,
  model,
  numberAttribute,
  OnInit,
  output,
  signal,
  viewChild,
} from '@angular/core';

import { generateId } from '@lc/util';
import { AbstractControl, ControlValueAccessor, ValidationErrors, Validator } from '@angular/forms';
import { CdkConnectedOverlay, CdkOverlayOrigin } from '@angular/cdk/overlay';
import { MatIcon } from '@angular/material/icon';
import { BspkChip } from '../chip';
import { DropdownOption, DropdownSize } from '../dropdown';
import { BspkFormField } from '../form-field';
import { BspkMenu, MAX_ITEM_COUNT, MIN_ITEM_COUNT } from '../menu';
import { noop, provideValidator, provideValueAccessor } from '../util';
import { ControlValidator } from '../util/control-validator';

@Component({
  selector: 'bspk-dropdown-chip-field',
  imports: [BspkFormField, BspkChip, BspkMenu, CdkConnectedOverlay, CdkOverlayOrigin, MatIcon],
  templateUrl: './dropdown-chip-field.component.html',
  styleUrl: './dropdown-chip-field.component.scss',
  providers: [provideValueAccessor(BspkDropdownChipField), provideValidator(BspkDropdownChipField)],
  host: {
    'data-bspk': 'dropdown-chip-field',
    '[attr.data-size]': 'size()',
    '[attr.data-readonly]': 'readOnly() || null',
    '[attr.aria-disabled]': 'disabled() || null',
    '[attr.aria-invalid]': '(invalid() || !!errorState()) || null',
  },
})
export class BspkDropdownChipField<TValue = string>
  extends ControlValidator
  implements ControlValueAccessor, Validator, DoCheck, OnInit {
  readonly value = model<TValue[]>([]);
  readonly options = input.required<DropdownOption<TValue>[]>();
  readonly label = input.required<string>();
  readonly disabled = input(false, { transform: booleanAttribute });
  readonly errorMessage = input<string>('');
  readonly helperText = input<string>('');
  /**
   * Determines how many items to display at a time
   * minimum: 3
   * maximum: 10
   * @default 5
   * @see BspkMenu.itemCount
   */
  readonly placeholder = input<string>('Select...');
  readonly readOnly = input(false, { transform: booleanAttribute });
  readonly required = input(false, { transform: booleanAttribute });
  readonly size = input<DropdownSize>('medium');
  readonly invalid = input(false, { transform: booleanAttribute });
  readonly controlId = input<string>(generateId('dropdown-chip-field'));
  readonly blur = output<FocusEvent>();
  readonly noResultsMessage = input<string>('');
  readonly itemCount = input(5, { alias: 'itemCount', transform: numberAttribute });
  readonly clampedItemCount = computed(() => Math.max(Math.min(this.itemCount(), MAX_ITEM_COUNT), MIN_ITEM_COUNT));

  readonly panelOpen = signal(false);

  readonly selectedOptions = computed(() => {
    const selectedValues = this.value();
    return this.options().filter((option) => selectedValues.includes(option.value));
  });

  readonly menuWidth = computed(
    () => `${this.trigger().nativeElement.getBoundingClientRect().width}px`,
  );

  protected readonly trigger = viewChild.required(CdkOverlayOrigin, { read: ElementRef });
  protected readonly menuId = generateId('menu');

  private onChangeFn: (value: TValue[]) => void = noop;
  private onTouchedFn: VoidFunction = noop;

  toggle() {
    if (this.panelOpen()) {
      this.closePanel();
    } else if (this.canOpen()) {
      this.panelOpen.set(true);
    }
  }

  handleTriggerClick(event: MouseEvent) {
    const target = event.target as HTMLElement | null;
    if (target?.closest('[data-remove]')) {
      event.stopPropagation();
      return;
    }

    this.toggle();
  }

  closePanel() {
    this.panelOpen.set(false);
  }

  protected canOpen(): boolean {
    return !this.panelOpen() && !this.disabled() && !this.readOnly() && this.options().length > 0;
  }

  addSelection(event: TValue[]): void {
    const selected = event[0];
    if (selected == null) {
      this.closePanel();
      return;
    }

    const nextValue = this.value().includes(selected)
      ? this.value()
      : [...this.value(), selected];

    this.value.set(nextValue);
    this.onChangeFn(nextValue);
    this.closePanel();
  }

  handleBlur(event: FocusEvent) {
    this.onTouchedFn();
    this.blur.emit(event);
  }

  removeOption(value: TValue): void {
    const nextValue = this.value().filter((selectedValue) => selectedValue !== value);
    this.value.set(nextValue);
    this.onChangeFn(nextValue);
  }

  registerOnChange(fn: any): void {
    this.onChangeFn = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouchedFn = fn;
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  setDisabledState(isDisabled: boolean): void {
    // noop
  }

  writeValue(obj: any): void {
    this.value.set(Array.isArray(obj) ? obj : []);
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  registerOnValidatorChange(fn: VoidFunction): void {
    // noop
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  validate(control: AbstractControl<TValue[]>): ValidationErrors | null {
    let errors: ValidationErrors | null = null;

    if (this.required() && this.value().length === 0) {
      errors ??= {};
      errors['required'] = true;
    }

    return errors;
  }
}
